import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DEFAULT_SITE } from './siteDefaults';
import {
  galleryAlbumExists,
  resolveAlbumCoverSrc,
  resolveGallery,
  resolveGalleryItemsForAlbum,
} from './siteGallery';
import { apiUrl } from './apiBase';
import MarketingSiteHeader from './components/MarketingSiteHeader';
import marketingHeaderNavIsCurrent from './marketingHeaderNavIsCurrent';

function ChevronLeft({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 18L9 12L15 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRight({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 18L15 12L9 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function decodeAlbumParam(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return '';
  try {
    return decodeURIComponent(raw).trim();
  } catch {
    return raw.trim();
  }
}

export default function GalleryAlbumPage() {
  const { albumId } = useParams();
  const [site, setSite] = useState(() => JSON.parse(JSON.stringify(DEFAULT_SITE)));
  const [ready, setReady] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => {
    fetch(apiUrl('/api/site'))
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('unreachable'))))
      .then(setSite)
      .catch(() => {})
      .finally(() => setReady(true));
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [albumId]);

  useEffect(() => {
    setLightboxIndex(-1);
  }, [albumId]);

  useEffect(() => {
    if (lightboxIndex >= 0) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [lightboxIndex]);

  const albumKey = useMemo(() => decodeAlbumParam(albumId || ''), [albumId]);

  const detail = useMemo(
    () => resolveGalleryItemsForAlbum(site, albumKey),
    [site.gallery, albumKey]
  );

  const gallery = useMemo(() => resolveGallery(site), [site.gallery]);

  const exists = useMemo(() => galleryAlbumExists(site, albumKey), [site.gallery, albumKey]);

  const albumCoverSrc = useMemo(
    () => resolveAlbumCoverSrc(site, albumKey),
    [site.gallery, albumKey]
  );

  useEffect(() => {
    const base = 'Rafid Seed';
    if (!ready) return undefined;
    if (!albumKey) {
      document.title = `Gallery | ${base}`;
      return () => {
        document.title = base;
      };
    }
    if (!exists && detail.items.length === 0) {
      document.title = `Album not found | ${base}`;
      return () => {
        document.title = base;
      };
    }
    document.title = `${detail.albumTitle || albumKey} | Gallery | ${base}`;
    return () => {
      document.title = base;
    };
  }, [ready, albumKey, detail.albumTitle, detail.items.length, exists]);

  const showNotFound = ready && albumKey && !exists && detail.items.length === 0;

  useEffect(() => {
    const n = detail.items.length;
    if (n === 0) {
      setLightboxIndex(-1);
      return;
    }
    if (lightboxIndex >= n) {
      setLightboxIndex(n - 1);
    }
  }, [detail.items.length, lightboxIndex]);

  useEffect(() => {
    if (lightboxIndex < 0) return undefined;
    const n = detail.items.length;
    if (n === 0) {
      setLightboxIndex(-1);
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIndex(-1);
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setLightboxIndex((i) => (i - 1 + n) % n);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setLightboxIndex((i) => (i + 1) % n);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxIndex, detail.items.length]);

  const lightboxItem =
    lightboxIndex >= 0 && lightboxIndex < detail.items.length ? detail.items[lightboxIndex] : null;

  const heroBackdrop = !showNotFound;

  return (
    <div className="min-h-screen bg-brand-cream/80 text-neutral-900">
      <MarketingSiteHeader
        site={site}
        idPrefix="gallery-album"
        alwaysSolid={!heroBackdrop}
        isNavLinkCurrent={marketingHeaderNavIsCurrent}
      />

      <div className={heroBackdrop ? 'relative isolate' : undefined}>
        {heroBackdrop && albumCoverSrc ? (
          <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
            <img
              src={albumCoverSrc}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-top"
              decoding="async"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-black/70" />
          </div>
        ) : null}
        {heroBackdrop && !albumCoverSrc ? (
          <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
            <div className="absolute inset-0 bg-gradient-to-br from-brand-olive/85 via-brand-forest/88 to-brand-forest/90" />
          </div>
        ) : null}

        {heroBackdrop ? (
          <div className="relative z-10 mx-auto max-w-shell px-4 pt-[calc(4.5rem+env(safe-area-inset-top))] sm:px-6 sm:pt-[calc(5rem+env(safe-area-inset-top))] lg:px-8">
            <nav className="mb-6 mt-10">
              <Link
                to="/gallery"
                className="text-xs font-medium text-white/90 underline-offset-4 hover:text-white hover:underline"
              >
                ← Back to albums
              </Link>
            </nav>
            <header className="pb-10 text-center md:pb-14">
              <div className="mx-auto flex max-w-4xl flex-col items-center justify-center px-4 pb-4 pt-2 sm:px-8 sm:pb-8 sm:pt-4 md:pb-12 md:pt-6">
                <p className="mb-3 text-sm font-bold uppercase tracking-wide text-white/90 drop-shadow">
                  {gallery.sectionLabel}
                </p>
                <h1 className="max-w-4xl text-3xl font-bold leading-tight tracking-tight text-white drop-shadow-md sm:text-4xl md:text-[2.5rem] md:leading-[1.2]">
                  {detail.albumTitle || albumKey}
                </h1>
              </div>
            </header>
          </div>
        ) : null}
      </div>

      <main className="relative z-10">
        <div
          className={
            heroBackdrop
              ? 'mx-auto max-w-shell px-4 pb-16 pt-0 sm:px-6 sm:pb-20 md:pb-24 lg:px-8'
              : 'mx-auto max-w-shell px-4 pb-16 pt-[calc(4.25rem+env(safe-area-inset-top))] sm:px-6 sm:pb-20 sm:pt-[calc(4.75rem+env(safe-area-inset-top))] md:pb-24 lg:px-8'
          }
        >
          {showNotFound ? (
            <div className="rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-sm">
              <h1 className="text-xl font-bold text-neutral-900">Album not found</h1>
              <p className="mt-2 text-sm text-neutral-600">
                There is no album matching this link.
              </p>
              <Link
                to="/gallery"
                className="mt-6 inline-flex min-h-[48px] items-center justify-center rounded-2xl border-0 bg-brand-lime px-6 py-3 text-sm font-semibold text-brand-forest no-underline hover:bg-brand-lime-hover"
              >
                View gallery
              </Link>
            </div>
          ) : (
            <>
              {detail.items.length === 0 ? (
                <p className="text-center text-sm text-neutral-600">No photos in this album yet.</p>
              ) : (
                <ul className="m-0 mt-10 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 lg:gap-5">
                  {detail.items.map(({ id, src, alt, title }, index) => {
                    const caption = typeof title === 'string' ? title.trim() : '';
                    return (
                      <li
                        key={id || `${src}-${index}`}
                        className="group flex flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-neutral-200/90 shadow-sm"
                      >
                        <button
                          type="button"
                          onClick={() => setLightboxIndex(index)}
                          className="aspect-[4/3] w-full shrink-0 cursor-zoom-in overflow-hidden border-0 bg-neutral-200/60 p-0 text-left"
                          aria-label={
                            caption ? `Open photo: ${caption}` : `Open photo ${index + 1} in large view`
                          }
                        >
                          <img
                            src={src}
                            alt={alt || caption || ''}
                            className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
                            loading={index < 6 ? 'eager' : 'lazy'}
                            decoding="async"
                            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 33vw, 25vw"
                          />
                        </button>
                        {caption ? (
                          <p className="m-0 border-t border-neutral-100/90 bg-brand-cream/50 px-3 py-2.5 text-center text-xs font-semibold leading-snug text-brand-forest sm:py-3 sm:text-sm">
                            {caption}
                          </p>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}

              {lightboxItem ? (
                <div
                  className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/80 p-3 backdrop-blur-sm sm:p-6"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Album photo preview"
                  onClick={() => setLightboxIndex(-1)}
                >
                  <button
                    type="button"
                    className="absolute right-3 top-3 inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-black/45 text-white transition-colors hover:bg-black/65 sm:right-5 sm:top-5"
                    aria-label="Close image preview"
                    onClick={() => setLightboxIndex(-1)}
                  >
                    <CloseIcon className="h-5 w-5" />
                  </button>
                  {detail.items.length > 1 ? (
                    <>
                      <button
                        type="button"
                        className="absolute left-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/45 text-white transition-colors hover:bg-black/65 sm:left-5"
                        aria-label="Previous image"
                        onClick={(e) => {
                          e.stopPropagation();
                          const n = detail.items.length;
                          setLightboxIndex((i) => (i - 1 + n) % n);
                        }}
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/45 text-white transition-colors hover:bg-black/65 sm:right-5"
                        aria-label="Next image"
                        onClick={(e) => {
                          e.stopPropagation();
                          const n = detail.items.length;
                          setLightboxIndex((i) => (i + 1) % n);
                        }}
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </>
                  ) : null}
                  <figure
                    className="m-0 max-h-[92vh] w-full max-w-[min(95vw,1200px)] overflow-hidden rounded-2xl bg-white/5 shadow-2xl ring-1 ring-white/20"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      src={lightboxItem.src}
                      alt={lightboxItem.alt || ''}
                      className="max-h-[82vh] w-full object-contain"
                    />
                    <figcaption className="flex items-center justify-between gap-3 bg-black/55 px-4 py-3 text-xs text-white/90 sm:px-5 sm:text-sm">
                      <span className="font-medium">
                        {typeof lightboxItem.title === 'string' && lightboxItem.title.trim()
                          ? lightboxItem.title.trim()
                          : 'Photo'}
                      </span>
                      <span className="shrink-0 text-white/70">
                        {lightboxIndex + 1} / {detail.items.length}
                      </span>
                    </figcaption>
                  </figure>
                </div>
              ) : null}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
